import HexBearing, { Bearing } from './HexBearing';

// Type for the pathfinding nodes used in the A* algorithm.
// It's a HexCoords instance monkey-patched with pathfinding data.
interface PathNode extends HexCoords {
  G?: number;
  H?: number;
  F?: number;
  parentNode?: PathNode;
}

// Plain object version of HexCoords, for type hints
interface HexCoordsData {
  q: number;
  r: number;
}

class HexCoords {
  // Factory method with overloads for type safety
  static get(coords: HexCoords): HexCoords;
  static get(coords: HexCoordsData): HexCoords;
  static get(coords: [number, number]): HexCoords;
  static get(q: number, r: number): HexCoords;
  static get(key: string): HexCoords;
  static get(...args: any[]): HexCoords {
    if (args.length === 1) {
      const arg = args[0];
      if (arg instanceof HexCoords) {
        return arg;
      } else if (Array.isArray(arg) && arg.length === 2) {
        return new HexCoords(arg[0], arg[1]);
      } else if (typeof arg === 'object' && arg !== null && 'q' in arg && 'r' in arg) {
        return new HexCoords(arg.q, arg.r);
      } else if (typeof arg === 'string' && arg.split('.').length == 2) {
        const qr = arg.split('.');
        const q = parseInt(qr[0]);
        const r = parseInt(qr[1]);
        return new HexCoords(q, r);
      }
    } else if (args.length === 2 && typeof args[0] === 'number' && typeof args[1] === 'number') {
      return new HexCoords(args[0], args[1]);
    }
    throw new Error(`unknown arguments to HexCoords.get. Got: ${JSON.stringify(args)}`);
  }

  static keyFor(q: number, r: number): string {
    return `${q}.${r}`;
  }

  q: number;
  r: number;

  constructor(q: number, r: number) {
    this.q = q;
    this.r = r;
  }

  get key(): string {
    return HexCoords.keyFor(this.q, this.r);
  }

  get x(): number {
    return this.q;
  }

  get y(): number {
    return this.r;
  }

  get z(): number {
    return (0 - this.q - this.r);
  }

  equalTo(other: HexCoords): boolean {
    if (!other) return false;
    return (this.q === other.q &&
            this.r === other.r);
  }

  absoluteDistanceFrom(other: HexCoords): number {
    return Math.max(
      Math.abs(other.x - this.x),
      Math.abs(other.y - this.y),
      Math.abs(other.z - this.z)
    );
  }

  absoluteDistanceFromNearest(goals: HexCoords[]): number {
    if (!goals || goals.length === 0) {
      return Infinity;
    }
    return Math.min(...goals.map(goal => this.absoluteDistanceFrom(goal)));
  }

  neighbor(bearing: Bearing, distance = 1): HexCoords {
    switch (bearing) {
    case HexBearing.Q:
      return new HexCoords(this.q + distance, this.r);
    case HexBearing.R:
      return new HexCoords(this.q, this.r + distance);
    case HexBearing.S:
      return new HexCoords(this.q - distance, this.r + distance);
    case HexBearing._Q:
      return new HexCoords(this.q - distance, this.r);
    case HexBearing._R:
      return new HexCoords(this.q, this.r - distance);
    case HexBearing._S:
      return new HexCoords(this.q + distance, this.r - distance);
    default:
      throw new Error("not possible");
    }
  }

  allNeighbors(): HexCoords[] {
    return HexBearing.all.map((b: Bearing) => this.neighbor(b));
  }

  isNeighbor(otherCoords: HexCoords): Bearing | false {
    for (const b of HexBearing.all) {
      if (this.neighbor(b as Bearing).equalTo(otherCoords)) {
        return b as Bearing;
      }
    }
    return false;
  }

  // alias because sometimes what we're asking is for the bearing
  bearingTo(otherCoords: HexCoords): Bearing | false {
    return this.isNeighbor(otherCoords);
  }

  is(otherCoords: HexCoords): boolean {
    return this.equalTo(otherCoords);
  }

  // A* pathfinding
  pathTo(toHex: HexCoords, options: { passableTiles?: Set<string>, initialBearing?: Bearing, turnCost?: number } = {}): HexCoords[] | null {
    const { passableTiles, initialBearing, turnCost = 1 } = options;
    const openNodes = new NodeSet([this as PathNode]);
    const closedNodes = new NodeSet([]);

    const cleanup = () => {
      const allVisitedNodes = [...openNodes.nodes, ...closedNodes.nodes];
      for (const node of allVisitedNodes) {
        delete node.G;
        delete node.H;
        delete node.F;
        delete node.parentNode;
      }
    };

    let currentNode: PathNode | undefined = this as PathNode;
    (this as PathNode).G = 0;

    while (openNodes.nodes.length > 0) {
      // Get the node with the lowest F score
      currentNode = openNodes.nodes.reduce((a, b) => ((a.F || Infinity) < (b.F || Infinity) ? a : b));

      if (currentNode.equalTo(toHex)) {
        // Path found, reconstruct it
        const path: HexCoords[] = [];
        let temp: PathNode | undefined = currentNode;
        while (temp && temp.parentNode) {
          path.push(temp);
          temp = temp.parentNode;
        }
        cleanup();
        return path.reverse();
      }

      openNodes.remove(currentNode);
      closedNodes.add(currentNode);

      const neighbors = currentNode.allNeighbors().filter(n => !closedNodes.contains(n as PathNode));

      for (const neighbor of neighbors) {
        if (passableTiles && !passableTiles.has(neighbor.key) && !neighbor.equalTo(toHex)) {
          continue;
        }

        let moveCost = 1; // Base cost for any move is 1 action.

        // If a turn cost is specified, factor it into the gScore.
        if (turnCost > 0) {
          const departureBearing = currentNode.isNeighbor(neighbor) as Bearing;

          let arrivalBearing: Bearing | undefined;
          if (currentNode.parentNode) {
            // This is not the first step; determine arrival bearing from the parent node.
            arrivalBearing = currentNode.parentNode.isNeighbor(currentNode) as Bearing;
          } else if (initialBearing) {
            // This is the first step; use the provided initial bearing.
            arrivalBearing = initialBearing;
          }

          if (arrivalBearing && arrivalBearing !== departureBearing) {
            // A turn is required, so add the turn cost.
            moveCost += turnCost;
          }
        }

        const gScore = (currentNode.G || 0) + moveCost;
        const neighborNode = neighbor as PathNode;

        if (!openNodes.contains(neighborNode) || gScore < (neighborNode.G || Infinity)) {
          neighborNode.parentNode = currentNode;
          neighborNode.G = gScore;
          neighborNode.H = neighborNode.absoluteDistanceFrom(toHex);
          neighborNode.F = neighborNode.G + neighborNode.H;

          if (!openNodes.contains(neighborNode)) {
            openNodes.add(neighborNode);
          }
        }
      }
    }

    cleanup();
    return null; // No path found
  }

  toString(): string {
    return `<HexCoords (${this.q}.${this.r})>`;
  }

  serialize(): { q: number, r: number } {
    return {
      q: this.q,
      r: this.r
    };
  }
}

export default HexCoords;

class NodeSet {
  array: boolean[][];
  nodes: PathNode[];

  constructor(initialNodes: PathNode[] = []) {
    this.array = [];
    this.nodes = [];
    for (const node of initialNodes) {
      this.add(node);
    }
  }

  add(node: PathNode): void {
    if (!this.contains(node)) {
      if (!this.array[node.q]) {
       this.array[node.q] = [];
      }
      this.array[node.q][node.r] = true;
      this.nodes.push(node);
    }
  }

  remove(node: PathNode): void {
    if (this.array[node.q]?.[node.r]) {
      this.array[node.q][node.r] = false;
      this.nodes = this.nodes.filter(n => !n.is(node));
    }
  }

  contains(node: PathNode): boolean {
    return !!this.array[node.q]?.[node.r];
  }
}
